import React, { useMemo } from 'react';
import { Layers, Archive } from 'lucide-react';
import PremiumDropdown, { DropdownOption } from '../../PremiumDropdown';
import { FullProject } from '../../../types';

export interface SubHeaderAnalysisControlsProps {
  viewMode: string;
  activeAnalysisTab: string;
  setActiveAnalysisTab: (tab: any) => void;
  selectedAnalysisProjectId: string;
  setSelectedAnalysisProjectId: (id: string) => void;
  dropdownProjects: FullProject[];
  setViewMode: (mode: any) => void;
  onPrefetch?: (view: string) => void;
}

export const SubHeaderAnalysisControls: React.FC<SubHeaderAnalysisControlsProps> = ({
  viewMode,
  activeAnalysisTab,
  setActiveAnalysisTab,
  selectedAnalysisProjectId,
  setSelectedAnalysisProjectId,
  dropdownProjects,
  setViewMode,
  onPrefetch
}) => {
  const analysisProjectOptions = useMemo(() => {
    const currentList = dropdownProjects.filter(p => {
      const lowerName = p.name.toLowerCase();
      const lowerId = p.id.toLowerCase();
      const forbiddenIds = ['inbox_inforum', 'kom_inforum', 'communication', 'inbox', 'posteingang', 'kommunikation'];
      const forbiddenKeywords = ['kommunikation', 'posteingang'];
      
      return !forbiddenIds.includes(lowerId) && !forbiddenKeywords.some(kw => lowerName.includes(kw));
    });

    const options: DropdownOption[] = currentList.map(p => ({
      value: p.id,
      label: p.archived ? `[ARCHIV] ${p.name}` : p.name,
      color: p.archived ? 'bg-slate-400' : p.color,
      icon: p.archived ? <Archive size={12} className="opacity-60" /> : undefined
    }));

    return [
      { value: '', label: 'Projekt wählen...', icon: <Layers size={13} /> },
      ...options
    ];
  }, [dropdownProjects]);

  return (
    <div className="flex items-center gap-3">
      <div data-tour="analysis-methods" className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 p-1 rounded-xl border border-slate-200/50 dark:border-slate-800 h-[34px] shadow-inner">
        <div className="flex items-center gap-0.5 overflow-x-auto no-scrollbar">
          {[
            { key: 'swot', label: 'SWOT' },
            { key: 'stakeholder', label: 'Stakeholder' },
            { key: 'risk', label: 'Risiko' },
            { key: 'toc', label: 'Wirkung' },
            { key: 'raci', label: 'RACI' },
            { key: 'participation', label: 'Partizipation' }
          ].map((tab) => (
            <button 
              key={tab.key}
              type="button"
              onClick={() => {
                React.startTransition(() => {
                  setViewMode('analysis');
                  setActiveAnalysisTab(tab.key);
                });
              }} 
              onMouseEnter={() => onPrefetch?.('analysis')}
              className={`px-3 h-full rounded-lg text-[10px] font-bold uppercase tracking-widest transition-all whitespace-nowrap ${
                viewMode === 'analysis' && activeAnalysisTab === tab.key 
                  ? 'bg-white dark:bg-slate-700 shadow-sm text-indigo-600' 
                  : 'text-slate-400 hover:text-slate-500'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="h-4 w-px bg-slate-200 dark:bg-slate-700 mx-1" />
        
        {/* Project Selector */}
        <PremiumDropdown
          value={selectedAnalysisProjectId}
          onChange={(val) => {
            setSelectedAnalysisProjectId(val);
          }}
          options={analysisProjectOptions}
          icon={<Layers size={13} />}
          placeholder="Projekt wählen"
          showSearch={analysisProjectOptions.length > 5}
          showLabel={true}
          className="h-full border-none shadow-none bg-transparent"
        />
      </div>
    </div>
  );
};
